import type { Writable } from "svelte/store";
import type { QuatrixConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Quatrix does not answer other origins. Calls go through /api/quatrix, which
// talks to the host of the account. The host and the API key are request
// headers. What is deleted goes to Quatrix's trash.

class FileSystemQuatrix extends FS {
  supportsDirRename = true;

  constructor(config: QuatrixConfig) {
    super(config);
  }

  get quatrixConfig(): QuatrixConfig {
    return this.config as QuatrixConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await quatrixCall(this.quatrixConfig, mkURL("/api/quatrix/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("quatrix list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the API key. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/quatrix/download", "path", path);
    let xhr = await quatrixSend("GET", url, this.quatrixConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/quatrix/upload", "path", path);
    await quatrixSend("PUT", url, this.quatrixConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/quatrix/rename", "path", path, "newPath", newPath);
    await quatrixCall(this.quatrixConfig, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await quatrixCall(this.quatrixConfig, mkURL("/api/quatrix/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await quatrixCall(this.quatrixConfig, mkURL("/api/quatrix/delete", "path", path), "POST");
  }
}

// What the user typed as the host of the account, as the server wants it.
export function quatrixHost(host: string): string {
  host = host.trim().toLowerCase();
  host = host.replace(/^https?:\/\//, "");
  let i = host.indexOf("/");
  if (i >= 0) {
    host = host.substring(0, i);
  }
  return host;
}

export function quatrixHeaders(config: QuatrixConfig): Record<string, string> {
  return {
    "X-Quatrix-Host": config.host,
    "X-Quatrix-Key": config.apiKey,
  };
}

export async function testQuatrixConnection(config: QuatrixConfig): Promise<void> {
  await quatrixCall(config, "/api/quatrix/test");
}

async function quatrixCall(config: QuatrixConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: quatrixHeaders(config),
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `quatrix ${rsp.status}`);
  }
  return js;
}

function quatrixSend(
  method: string,
  url: string,
  config: QuatrixConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(quatrixHeaders(config))) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      quatrixErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`quatrix ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("quatrix request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function quatrixErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `quatrix ${xhr.status}`;
}

export function makeFileSystemQuatrix(config: QuatrixConfig): FileSystemQuatrix {
  return new FileSystemQuatrix(config);
}
